package com.arena_web.ui.sections.news

import androidx.compose.runtime.*
import com.arena_web.data.dto.FootballMatchDto
import com.arena_web.data.dto.TeamDetailDto
import com.arena_web.ui.components.widgets.SkeletonBox
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.utils.s
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

// =============================================================================
// Club detail PAGE (/news/club/{id}) — banner hero with the badge block
// overlapping it, form dots, history/calendar, squad grid. Same design
// language as the news-article and match detail pages.
// =============================================================================

@Composable
fun ClubDetailPageContent(
    sport: String,
    team: TeamDetailDto?,
    failed: Boolean,
    mobile: Boolean,
    onOpenPlayer: (String) -> Unit = {},
) {
    val t = LocalAppStrings.current
    val ctx = rememberPageContext()

    Div(
        attrs = {
            style {
                property("width", "100%")
                property("flex-grow", "1")
                property("min-height", "0")
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("scrollbar-width", "none")
            }
        }
    ) {
        val banner = team?.banner
        if (mobile) {
            Box(modifier = Modifier.fillMaxWidth().styleModifier { property("position", "relative") }) {
                if (banner != null) {
                    Img(
                        src = banner, alt = "",
                        attrs = Modifier.fillMaxWidth().styleModifier {
                            property("height", "160px")
                            property("object-fit", "cover")
                            property("display", "block")
                        }.toAttrs()
                    )
                    Div(attrs = {
                        style {
                            property("position", "absolute")
                            property("inset", "0")
                            property("background", "linear-gradient(180deg, rgba(2,4,11,0.45) 0%, rgba(2,4,11,0) 40%, rgba(2,4,11,0.85) 100%)")
                            property("pointer-events", "none")
                        }
                    })
                } else {
                    Div(attrs = { style { property("width", "100%"); property("height", "64px") } })
                }
                FloatingBackButton { navigateBack(ctx) }
            }
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    if (!mobile) {
                        property("max-width", s(860))
                        property("margin", "0 auto")
                        property("padding-top", s(8))
                    } else {
                        property("padding", "0 16px")
                        if (banner != null) property("margin-top", "-34px")
                        property("position", "relative")
                    }
                    property("gap", if (mobile) "16px" else s(18))
                }
        ) {
            if (!mobile) {
                DesktopBackRow { navigateBack(ctx) }
                if (banner != null) {
                    Img(
                        src = banner, alt = "",
                        attrs = Modifier.fillMaxWidth().styleModifier {
                            property("height", s(220))
                            property("object-fit", "cover")
                            property("display", "block")
                            property("border-radius", s(24))
                        }.toAttrs()
                    )
                }
            }

            if (failed && team == null) {
                SpanText(
                    t.newsDetailUnavailable,
                    modifier = Modifier.fillMaxWidth().fontSize(13.px)
                        .styleModifier { property("text-align", "center"); property("color", "var(--arena-text-3)") }
                )
            }

            if (team == null && !failed) {
                // Shimmer placeholders while the club profile loads.
                SkeletonBox(height = 120.px, borderRadius = 20.px)
                SkeletonBox(height = 80.px, borderRadius = 14.px)
                SkeletonBox(height = 180.px, borderRadius = 18.px)
            }

            team?.let { club ->
                ClubHeaderCard(club, mobile)

                club.description?.takeIf { it.isNotBlank() }?.let { descr ->
                    SpanText(
                        descr,
                        modifier = Modifier.fontSize(if (mobile) 13.px else 15.px)
                            .styleModifier {
                                property("color", "var(--arena-text-2)")
                                property("line-height", "1.6")
                                property("display", "-webkit-box")
                                property("-webkit-line-clamp", "6")
                                property("-webkit-box-orient", "vertical")
                                property("overflow", "hidden")
                            }
                    )
                }

                if (club.lastMatches.isNotEmpty()) {
                    ClubMatchList(t.newsClubLastMatches, club.lastMatches, sport, mobile)
                }
                if (club.nextMatches.isNotEmpty()) {
                    ClubMatchList(t.newsClubNextMatches, club.nextMatches.take(5), sport, mobile)
                }

                if (club.squad.isNotEmpty()) {
                    SquadGrid(club, mobile, onOpenPlayer)
                }
            }
            Div(attrs = { style { property("width", "100%"); property("height", if (mobile) "96px" else "40px"); property("flex-shrink", "0") } })
        }
    }
}

// ── Header card: badge, name, meta, form dots ──

@Composable
private fun ClubHeaderCard(
    club: TeamDetailDto,
    mobile: Boolean,
) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth()
            .styleModifier {
                property("border", "0.5px solid ${AppGradients.NewsCardBorder}")
                property("border-radius", if (mobile) "20px" else s(24))
                property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                property("padding", if (mobile) "16px 14px" else "${s(22)} ${s(24)}")
                property("gap", if (mobile) "12px" else s(14))
                property("box-shadow", "0 12px 32px rgba(2, 4, 11, 0.45)")
            }
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().styleModifier { property("gap", if (mobile) "14px" else s(18)) },
            verticalAlignment = Alignment.CenterVertically
        ) {
            club.badge?.let { badge ->
                Img(
                    src = badge, alt = "",
                    attrs = Modifier.styleModifier {
                        property("width", if (mobile) "60px" else s(80))
                        property("height", if (mobile) "60px" else s(80))
                        property("object-fit", "contain")
                        property("flex-shrink", "0")
                        property("filter", "drop-shadow(0 4px 10px rgba(2,4,11,0.5))")
                    }.toAttrs()
                )
            }
            Column(modifier = Modifier.styleModifier { property("min-width", "0"); property("gap", "4px") }) {
                SpanText(
                    club.name,
                    modifier = Modifier.fontSize(if (mobile) 20.px else 26.px).fontWeight(800)
                        .styleModifier { property("color", "var(--arena-text-1)") }
                )
                val meta = listOfNotNull(
                    club.stadium,
                    club.location,
                    club.formedYear?.let { "${t.newsClubFounded} $it" },
                ).joinToString(" · ")
                if (meta.isNotBlank()) {
                    SpanText(
                        meta,
                        modifier = Modifier.fontSize(if (mobile) 11.px else 13.px)
                            .styleModifier { property("color", "var(--arena-text-3)"); property("line-height", "1.5") }
                    )
                }
            }
        }
        // Form dots from finished recent matches (newest last).
        val form = club.lastMatches.mapNotNull { formOf(it, club.name) }
        if (form.isNotEmpty()) {
            Row(modifier = Modifier.styleModifier { property("gap", "6px") }, verticalAlignment = Alignment.CenterVertically) {
                form.reversed().forEach { r ->
                    Div(attrs = {
                        style {
                            property("width", if (mobile) "22px" else "24px")
                            property("height", if (mobile) "22px" else "24px")
                            property("border-radius", "100px")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property(
                                "background",
                                when (r) {
                                    'W' -> "rgba(34, 197, 94, 0.18)"
                                    'L' -> "rgba(239, 68, 68, 0.18)"
                                    else -> "rgba(148, 163, 184, 0.15)"
                                }
                            )
                            property(
                                "border",
                                "1px solid " + when (r) {
                                    'W' -> "rgba(34, 197, 94, 0.55)"
                                    'L' -> "rgba(239, 68, 68, 0.55)"
                                    else -> "rgba(148, 163, 184, 0.4)"
                                }
                            )
                        }
                    }) {
                        SpanText(
                            r.toString(),
                            modifier = Modifier.fontSize(10.px).fontWeight(700)
                                .styleModifier {
                                    property(
                                        "color",
                                        when (r) {
                                            'W' -> "#22C55E"
                                            'L' -> "#EF4444"
                                            else -> "#94A3B8"
                                        }
                                    )
                                }
                        )
                    }
                }
            }
        }
    }
}

/** W/D/L of one finished match from the club's perspective; null if unplayed. */
private fun formOf(match: FootballMatchDto, clubName: String): Char? {
    val home = match.homeScore ?: return null
    val away = match.awayScore ?: return null
    val isHome = sameSideTeam(match.homeTeam, clubName)
    val my = if (isHome) home else away
    val their = if (isHome) away else home
    return when {
        my > their -> 'W'
        my < their -> 'L'
        else -> 'D'
    }
}

// ── Match history / calendar ──

@Composable
private fun ClubMatchList(
    title: String,
    matches: List<FootballMatchDto>,
    sport: String,
    mobile: Boolean,
) {
    val ctx = rememberPageContext()
    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "10px") }) {
        SpanText(
            title,
            modifier = Modifier.fontSize(if (mobile) 14.px else 16.px).fontWeight(700)
                .styleModifier { property("color", "var(--arena-text-1)") }
        )
        SectionCard {
            matches.forEachIndexed { index, match ->
                if (index > 0) DetailRowDivider()
                Row(
                    modifier = Modifier.fillMaxWidth().cursor(Cursor.Pointer)
                        .onClick { openMatchPage(ctx, sport, match) }
                        .styleModifier { property("padding", if (mobile) "10px 12px" else "12px 18px"); property("gap", "10px") },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.styleModifier { property("flex", "0 0 44px"); property("gap", "1px") }) {
                        SpanText(
                            kickoffDateLabel(match.utcDate),
                            modifier = Modifier.fontSize(if (mobile) 10.px else 11.px)
                                .styleModifier { property("color", "var(--arena-text-3)") }
                        )
                    }
                    Column(modifier = Modifier.styleModifier { property("flex", "1 1 0"); property("min-width", "0"); property("gap", "2px") }) {
                        Row(
                            modifier = Modifier.styleModifier { property("gap", "5px"); property("min-width", "0") },
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            MiniCrest(match.homeCrest)
                            SpanText(
                                match.homeTeam,
                                modifier = clubMatchTeamModifier(mobile)
                            )
                            SpanText(
                                "–",
                                modifier = Modifier.fontSize(if (mobile) 11.px else 12.px)
                                    .styleModifier { property("color", "var(--arena-text-3)"); property("flex-shrink", "0") }
                            )
                            MiniCrest(match.awayCrest)
                            SpanText(
                                match.awayTeam,
                                modifier = clubMatchTeamModifier(mobile)
                            )
                        }
                        if (match.competitionName.isNotBlank()) {
                            SpanText(
                                match.competitionName,
                                modifier = Modifier.fontSize(if (mobile) 10.px else 11.px)
                                    .styleModifier { property("color", "var(--arena-text-3)") }
                            )
                        }
                    }
                    SpanText(
                        if (match.homeScore != null && match.awayScore != null) {
                            "${match.homeScore}:${match.awayScore}"
                        } else {
                            kickoffTimeLabel(match.utcDate)
                        },
                        modifier = Modifier.fontSize(if (mobile) 12.px else 13.px).fontWeight(700)
                            .styleModifier {
                                property("flex-shrink", "0")
                                property("color", "var(--arena-text-1)")
                                property("font-variant-numeric", "tabular-nums")
                            }
                    )
                }
            }
        }
    }
}

@Composable
private fun MiniCrest(url: String) {
    if (url.isNotBlank()) {
        Img(
            src = url, alt = "",
            attrs = Modifier.styleModifier {
                property("width", "16px")
                property("height", "16px")
                property("object-fit", "contain")
                property("flex-shrink", "0")
            }.toAttrs()
        )
    }
}

private fun clubMatchTeamModifier(mobile: Boolean): Modifier =
    Modifier.fontSize(if (mobile) 12.px else 13.px).fontWeight(500)
        .styleModifier {
            property("color", "var(--arena-text-1)")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
            property("white-space", "nowrap")
            property("min-width", "0")
        }

// ── Squad ──

@Composable
private fun SquadGrid(
    club: TeamDetailDto,
    mobile: Boolean,
    onOpenPlayer: (String) -> Unit,
) {
    val t = LocalAppStrings.current
    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "10px") }) {
        SpanText(
            t.newsClubSquad,
            modifier = Modifier.fontSize(if (mobile) 14.px else 16.px).fontWeight(700)
                .styleModifier { property("color", "var(--arena-text-1)") }
        )
        SectionCard {
            // Real multi-cell grid → raw Div (Kobweb Box is a stacking grid).
            Div(attrs = {
                style {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(${if (mobile) 3 else 5}, 1fr)")
                    property("gap", if (mobile) "16px 8px" else "20px 12px")
                    property("width", "100%")
                    property("padding", if (mobile) "14px 12px" else "20px 18px")
                }
            }) {
                club.squad.forEach { player ->
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        modifier = Modifier
                            .styleModifier { property("gap", "5px"); property("min-width", "0") }
                            .then(
                                player.id?.let { id -> Modifier.cursor(Cursor.Pointer).onClick { onOpenPlayer(id) } }
                                    ?: Modifier
                            )
                    ) {
                        if (!player.photo.isNullOrBlank()) {
                            Img(
                                src = player.photo, alt = "",
                                attrs = Modifier.styleModifier {
                                    property("width", if (mobile) "56px" else "68px")
                                    property("height", if (mobile) "56px" else "68px")
                                    property("border-radius", "100px")
                                    property("object-fit", "cover")
                                    property("object-position", "top")
                                    property("background", "radial-gradient(circle at 50% 30%, rgba(60,121,241,0.22), rgba(60,121,241,0.05))")
                                    property("border", "0.5px solid rgba(180, 204, 246, 0.16)")
                                }.toAttrs()
                            )
                        } else {
                            Div(attrs = {
                                style {
                                    property("width", if (mobile) "56px" else "68px")
                                    property("height", if (mobile) "56px" else "68px")
                                    property("border-radius", "100px")
                                    property("background", "rgba(60, 121, 241, 0.10)")
                                    property("border", "0.5px solid rgba(180, 204, 246, 0.16)")
                                }
                            })
                        }
                        SpanText(
                            player.name,
                            modifier = Modifier.fontSize(if (mobile) 10.px else 11.px).fontWeight(600)
                                .styleModifier {
                                    property("color", "var(--arena-text-1)")
                                    property("text-align", "center")
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                    property("white-space", "nowrap")
                                    property("max-width", "100%")
                                }
                        )
                        player.position?.let { pos ->
                            SpanText(
                                pos,
                                modifier = Modifier.fontSize(if (mobile) 9.px else 10.px)
                                    .styleModifier { property("color", "var(--arena-text-3)"); property("text-align", "center") }
                            )
                        }
                    }
                }
            }
        }
    }
}

/** Navigate to a match detail page, seeding the nav session so it paints
 *  instantly; the URL carries the resolve context for hard reloads. */
fun openMatchPage(
    ctx: com.varabyte.kobweb.core.PageContext,
    sport: String,
    match: FootballMatchDto,
) {
    MatchNavSession.match = match
    val encode: (String) -> String = { v -> js("encodeURIComponent")(v) as String }
    val query = buildList {
        add("sport=$sport")
        add("date=${match.utcDate.take(10)}")
        if (match.competitionCode.isNotBlank()) add("comp=${encode(match.competitionCode)}")
        if (match.competitionName.isNotBlank()) add("compName=${encode(match.competitionName)}")
        add("home=${encode(match.homeTeam)}")
        add("away=${encode(match.awayTeam)}")
        if (match.homeCrest.isNotBlank()) add("homeCrest=${encode(match.homeCrest)}")
        if (match.awayCrest.isNotBlank()) add("awayCrest=${encode(match.awayCrest)}")
    }.joinToString("&")
    ctx.router.navigateTo("/news/match/${match.id}?$query")
}
